
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, ExternalLink, ArrowLeft } from "lucide-react";
import { JobListingsModal } from "@/components/JobListingsModal";
import { toast } from "@/hooks/use-toast";
import PageHead from "@/components/PageHead";
import { supabase } from "@/integrations/supabase/client";

interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  type: string;
  postedDate: string;
  description: string;
  link: string;
  hasVisaSponsorship?: boolean;
}

const JobDetailsPage = () => {
  const { jobId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(30);
  const [countdownComplete, setCountdownComplete] = useState(false);
  const [showListingsModal, setShowListingsModal] = useState(false);
  const [job, setJob] = useState<Job | null>(location.state?.job || null);
  const [loading, setLoading] = useState(!location.state?.job);
  const [error, setError] = useState<string | null>(null);

  // Fetch job if not provided in location state
  useEffect(() => {
    const fetchJobDetails = async () => {
      if (!jobId) {
        navigate("/visa-sponsored-jobs");
        return;
      }
      
      if (!job) {
        setLoading(true);
        try {
          // Try fetching from Supabase first
          const { data, error } = await supabase
            .from('job_listings')
            .select('*')
            .eq('id', jobId)
            .single();
          
          if (data) {
            // Map Supabase data to Job interface
            setJob({
              id: data.id,
              title: data.title,
              company: data.company,
              location: data.location,
              type: data.job_type,
              postedDate: data.posted_date,
              description: data.description,
              link: data.link,
              hasVisaSponsorship: data.has_visa_sponsorship
            });
          } else {
            // If job not found in database, generate fallback data
            const fallbackJob = generateFallbackJob(jobId);
            setJob(fallbackJob);
          }
        } catch (err) {
          console.error("Error fetching job details:", err);
          const fallbackJob = generateFallbackJob(jobId);
          setJob(fallbackJob);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchJobDetails();
  }, [jobId, job, navigate]);

  // Generate fallback job data when job is not found
  const generateFallbackJob = (id: string): Job => {
    const jobTypes = ['Software Engineer', 'Data Scientist', 'Product Manager', 'Marketing Specialist', 'UX Designer'];
    const companies = ['Tech Solutions Inc.', 'Data Analytics Co.', 'Innovative Products', 'Global Marketing Agency', 'Design Studio'];
    const locations = ['United States', 'Canada', 'United Kingdom'];
    
    // Use jobId hash to generate consistent data
    const hashValue = Array.from(id).reduce((acc, char) => acc + char.charCodeAt(0), 0);
    
    return {
      id: id,
      title: jobTypes[hashValue % jobTypes.length],
      company: companies[hashValue % companies.length],
      location: locations[hashValue % locations.length],
      type: "Full-time",
      postedDate: "Recently posted",
      description: `This position offers visa sponsorship for qualified candidates. International applicants are welcome to apply.

Key Responsibilities:
- Drive strategic initiatives and projects from inception to completion
- Collaborate with cross-functional teams to achieve business objectives
- Create and implement innovative solutions to complex problems
- Analyze data to identify trends and make recommendations
- Present findings and insights to stakeholders

Requirements:
- Bachelor's degree in a relevant field
- 3+ years of experience in a similar role
- Strong analytical and problem-solving skills
- Excellent written and verbal communication
- Ability to work in a fast-paced environment`,
      link: "https://visanow.io/upgrade",
      hasVisaSponsorship: true
    };
  };

  useEffect(() => {
    if (countdown <= 0) {
      setCountdownComplete(true);
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  const handleJobPageClick = () => {
    setShowListingsModal(true);
  };

  const handleModalSkip = () => {
    if (!countdownComplete) {
      toast({
        title: "Please Wait",
        description: `You need to wait ${countdown} more second${countdown !== 1 ? "s" : ""} before you can skip.`,
        variant: "default",
      });
      return;
    }
    setShowListingsModal(false);
    window.open(job?.link, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow container mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-10 w-32 bg-gray-200 rounded mb-6"></div>
            <div className="h-40 bg-gray-200 rounded mb-6"></div>
            <div className="h-80 bg-gray-200 rounded"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow container mx-auto px-4 py-8 text-center">
          <h1 className="text-2xl font-bold mb-4">Job Not Found</h1>
          <p className="text-gray-600 mb-6">The job you are looking for does not exist or has been removed.</p>
          <Button onClick={() => navigate("/visa-sponsored-jobs")}>
            Browse Jobs
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <PageHead 
        title={job?.title || "Job Details"} 
        description={job?.description?.substring(0, 155) || "View details for this visa-sponsored job opportunity"} 
      />
      <Navbar />

      <main className="flex-grow container mx-auto px-4 py-8">
        <Button
          variant="outline"
          onClick={() => navigate("/visa-sponsored-jobs")}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Jobs
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">{job.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <Progress value={((30 - countdown) / 30) * 100} className="h-2 mb-4" />

            {!countdownComplete && (
              <div className="text-gray-600 mb-4">
                Please wait {countdown} seconds so we load the job description.
              </div>
            )}

            {!countdownComplete && (
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <h3 className="font-medium text-lg mb-4">Job Preparation Tips</h3>
                  <ul className="space-y-3 list-disc pl-4 text-gray-600">
                    <li>Update your resume to highlight relevant skills and experience that match the job requirements</li>
                    <li>Research the company's culture, values, and recent achievements</li>
                    <li>Prepare examples of your past work that demonstrate your expertise</li>
                    <li>Review common interview questions for similar positions</li>
                    <li>Understand the visa sponsorship process and required documentation</li>
                    <li>Consider preparing a portfolio of your work if applicable</li>
                    <li>Practice explaining your motivation for seeking an international role</li>
                  </ul>
                </CardContent>
              </Card>
            )}

            {countdownComplete && (
              <div className="space-y-6">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-sm text-gray-500 mb-4">
                      {job.company} • {job.location} • {job.type}
                    </div>

                    <div className="space-y-4">
                      <h3 className="font-medium text-lg">Job Description</h3>
                      <p className="whitespace-pre-line">{job.description}</p>

                      <div className="flex items-center text-sm text-blue-600 font-medium mt-2">
                        This job offers visa sponsorship for qualified candidates
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Button
                  onClick={handleJobPageClick}
                  className="flex items-center mt-4"
                >
                  Visit Job Page Now <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      <Footer />

      <JobListingsModal
        isOpen={showListingsModal}
        onClose={() => setShowListingsModal(false)}
        onSkip={handleModalSkip}
      />
    </div>
  );
};

export default JobDetailsPage;
